import type { ComponentProps, FC, ReactNode } from 'react';
import React, { useCallback, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, xcss } from '@atlaskit/primitives';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext';

import { SectionMessageButtonConfig } from '../SectionMessage/ChangeEditionSectionMessage';
import { UpgradeEditionButton } from '../Upgrade/UpgradeEditionButton';
import { GradientSubtleLinkButton } from '../GradientLooknFeel/GradientSubtleLinkButton';
import { ConfluenceEdition } from '../Upgrade/__types__/waitForEditionChangeQuery';

import { SectionMessage } from './SectionMessage';
import type { SectionMessageProps } from './SectionMessage';
import { EndUserRequestUpgradeButton } from '@confluence/experiment-confluence-enable-end-user-request/entry-points/useEnableEndUserRequestUpgrade';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BodyWrapper = styled.div({
	display: 'flex',
	gap: token('space.150'),
	flexGrow: 1,
	justifyContent: 'space-between',
});

interface MessageWrapperProps {
	topBanner?: boolean;
}

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
const MessageWrapper = styled.div<MessageWrapperProps>((props) =>
	props?.topBanner
		? {
				gap: token('space.150'),
				display: 'grid',
				gridTemplateColumns: '1fr auto',
				alignItems: 'start',
			}
		: {
				display: 'inline-block',
				flexDirection: 'column',
			},
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LearnMoreLink = styled.a({
	fontWeight: token('font.weight.medium'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- Ignored via go/DSP-18766
	color: `${token('color.link')} !important`,
	cursor: 'pointer',
});

const messageWrapperBannerStyles = xcss({
	display: 'block',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

const messageWrapperMessageStyles = xcss({
	display: 'inline',
});

type MiniSectionMessageProps = Omit<SectionMessageProps, 'buttonConfig' | 'body'> & {
	messageContent: ComponentProps<typeof FormattedMessage>;
	dismissButton?: ReactNode;
};

export const MiniSectionMessage: FC<MiniSectionMessageProps> = ({
	messageContent,
	dismissButton,
	learnMoreLink,
	showRequestUpgradeButton,
	showRequestUpgradeButtonAATest,
	topBanner = false,
	targetEdition = ConfluenceEdition.PREMIUM,
	...rest
}) => {
	const { source, upgradeEditionActionSubjectId, onUpgradeButtonClick } = rest;
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { edition } = useSessionData();
	const { formatMessage } = useIntl();
	const { isSiteAdmin } = useContext(SPAViewContext);

	const LearnMoreLinkComponent =
		learnMoreLink?.appearance === 'subtle-link' ? GradientSubtleLinkButton : LearnMoreLink;

	const onLearnMoreClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'freeLearnMore',
				source,
				attributes: {
					currentEdition: edition,
					targetEdition,
					isSiteAdmin,
					linkMessage: learnMoreLink?.messageDescriptor?.defaultMessage,
				},
			},
		}).fire();
	}, [createAnalyticsEvent, source, edition, targetEdition, isSiteAdmin, learnMoreLink]);

	return (
		<SectionMessage
			body={
				<BodyWrapper>
					<MessageWrapper topBanner={topBanner}>
						<Box xcss={topBanner ? messageWrapperBannerStyles : messageWrapperMessageStyles}>
							<FormattedMessage {...messageContent} />{' '}
						</Box>
						{isSiteAdmin ? (
							<UpgradeEditionButton
								analyticsProps={{
									actionSubjectId: upgradeEditionActionSubjectId || '',
									source,
									attributes: {
										isSiteAdmin: true,
									},
								}}
								appearance="subtle-link"
								targetEdition={targetEdition}
								onUpgradeButtonClick={onUpgradeButtonClick}
							/>
						) : (
							(showRequestUpgradeButtonAATest && <div /> && showRequestUpgradeButton && (
								<EndUserRequestUpgradeButton />
							)) ||
							(learnMoreLink?.messageDescriptor && (
								<LearnMoreLinkComponent
									href={learnMoreLink?.href}
									onClick={() => {
										onLearnMoreClick();
										learnMoreLink?.onClick?.();
									}}
									target={learnMoreLink?.target}
								>
									{formatMessage(learnMoreLink.messageDescriptor)}
								</LearnMoreLinkComponent>
							))
						)}
					</MessageWrapper>
					{dismissButton}
				</BodyWrapper>
			}
			buttonConfig={SectionMessageButtonConfig.NONE}
			{...rest}
		/>
	);
};
